import { Box, Button, Card, CardContent, CardHeader, FormHelperText, Grid, Drawer, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { RecPatch } from "@/dataProvider/tools";
import { RetentionRuleResponseDto } from "@/gen/api/governance";


const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(100).required('Name is required'),
    days: Yup.number().positive().integer().required('Days is required'),
  })

const EditRetentionRule: FC<{
  open: boolean;
  setOpen: (x: boolean) => void;
}> = ({ open, setOpen }) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const record = useRecordContext<RetentionRuleResponseDto & RecPatch>()
  const mode = record !== null && record !== undefined ? 'edit' : 'create'
  const save = useSimpleSaveAction()
  
  const initialValues = {
    submit: '',
    name: record?.name || '',
    days: record?.days || 7,
  }

  const preDataRef = useRef<boolean | null>(!!record)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setOpen(false)
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    save(mode === 'create' ? values : {...record, ...values}, options)
  }, [save, mode, setOpen])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm, setValues } = formikCtrl
  const handleClose = useCallback(() => { 
    setOpen(false) 
    preDataRef.current = null
    resetForm({
      values: { name: '', days: 7, submit: '', }
    })
  }, [setOpen, resetForm])


  useEffect(() => {
    if (!preDataRef.current && !!record) {
      preDataRef.current = true
      setValues({
        name: record.name || '',
        days: record.days || '',
      } as any)
    }
  }, [record?.id, open, setValues])

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title={mode === 'create' ? "New Retention Rule" : "Edit Retention Rule"} />
          <CardContent>
            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} >
                <TextField label="Name*" name="name" />
              </Grid>
              <Grid item xs={12} >
                <TextField label="Days*" name="days" />
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Card>

      </OdForm>
    </Drawer>
  )
}

export default EditRetentionRule;
